// ============================================================
// TtrPlayersPanel — 플레이어 점수/카드수/티켓수/기차 잔량 패널
//   SoT: H:\AI\AI_Setting\보드겜돌이\티켓투라이드.md §3-1 / §3-A 좌석 5색
//   SEQ=10146 — 현재 턴 펄스 강조 + finalTurnTrigger 표기
// ============================================================
import React from 'react'
import { TtrPlayer, TtrOtherCounts } from '../store/ticket'

// SoT §3-A v2 (SEQ=10148) — YELLOW만 노선 톤과 구별 강화 (#E8C235 → #D4A91C)
const SEAT_COLOR_HEX: Record<string, string> = {
  RED:    '#C81E1E',
  BLUE:   '#1957B5',
  GREEN:  '#2E8E3E',
  YELLOW: '#D4A91C',
  BLACK:  '#222222',
}

// ── Longest Path 트로피 SVG 아이콘 (SoT §3-A v2.1, SEQ=10149) ─
// 골드 톤 #D4A91C (YELLOW 좌석과 동일). 14×14 inline SVG.
// emoji 의존성 제거 — 시스템 폰트 무관 일관 렌더.
const LongestPathTrophyIcon: React.FC = () => (
  <svg
    className="ttr-longest-trophy"
    width="14"
    height="14"
    viewBox="0 0 24 24"
    role="img"
    aria-label="최장 경로 보너스"
    focusable="false"
  >
    {/* 트로피 body — 컵 형태 */}
    <path
      d="M 7 3 H 17 V 4 H 19 Q 20 4 20 5 V 7 Q 20 9 18 9.5 V 10 Q 18 14 14 15 V 17 H 16 V 19 H 8 V 17 H 10 V 15 Q 6 14 6 10 V 9.5 Q 4 9 4 7 V 5 Q 4 4 5 4 H 7 Z"
      fill="#D4A91C"
      stroke="#A07F0F"
      strokeWidth="0.6"
      strokeLinejoin="round"
    />
    {/* 받침대 */}
    <rect x="7" y="19" width="10" height="2" fill="#A07F0F" rx="0.5" />
    {/* 광택 highlight (subtle) */}
    <path
      d="M 9 5 V 10 Q 9 12 11 13"
      fill="none"
      stroke="rgba(255,255,255,0.4)"
      strokeWidth="0.8"
      strokeLinecap="round"
    />
  </svg>
)

export interface TtrPlayersPanelProps {
  players:                   TtrPlayer[]
  currentTurnUserCode:       number | null
  finalTurnTriggerUserCode?: number | null
  myUserCode:                number | null
  otherCounts:               Record<number, TtrOtherCounts>
  myHandTotalCount:          number  // 본인 색별 합계 (otherCounts에 없음, 외부 계산)
  myTicketCount:             number
}

const TtrPlayersPanel: React.FC<TtrPlayersPanelProps> = ({
  players, currentTurnUserCode, finalTurnTriggerUserCode,
  myUserCode, otherCounts, myHandTotalCount, myTicketCount,
}) => {
  return (
    <section className="ttr-players-panel">
      <h3>플레이어 ({players.length})</h3>
      <ul className="ttr-players-list">
        {players.map(pl => {
          const isMe       = pl.userCode === myUserCode
          const isTurn     = pl.userCode === currentTurnUserCode
          const isTrigger  = pl.userCode === finalTurnTriggerUserCode
          const cardCount  = isMe ? myHandTotalCount : (otherCounts[pl.userCode]?.cardCount ?? 0)
          const ticketCnt  = isMe ? myTicketCount    : (otherCounts[pl.userCode]?.ticketCount ?? 0)
          const colorHex   = SEAT_COLOR_HEX[pl.color] ?? '#666666'
          return (
            <li
              key={pl.userCode}
              className={
                'ttr-player-row' +
                (isMe ? ' me' : '') +
                (isTurn ? ' turn' : '')
              }
            >
              <span
                className="ttr-player-color-dot"
                style={{ backgroundColor: colorHex }}
                aria-label={pl.color}
              />
              <span className="ttr-player-name">
                {pl.nickname}{isMe && ' (나)'}
                {isTrigger && <span className="ttr-trigger-badge"> 최종</span>}
                {pl.isLongestPath && (
                  <span
                    className="ttr-longest-badge"
                    title="최장 경로 보너스 +10"
                    aria-label="최장 경로 보너스"
                  >
                    <LongestPathTrophyIcon />
                  </span>
                )}
              </span>
              <span className="ttr-player-score">{pl.score}점</span>
              <span className="ttr-player-stats">
                카드 {cardCount} · 티켓 {ticketCnt} · 🚂 {pl.trainRemain}
              </span>
            </li>
          )
        })}
      </ul>
    </section>
  )
}

export default TtrPlayersPanel
